/**
 * Unified hub Control Bridge — `/hub` and `/render/me/control-bridge`.
 * Loads personal-model manifest with cross-domain-attention strip.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Spin, Typography } from 'antd';
import { ControlBridgeShell } from './ControlBridgeShell';
import {
  fetchRenderManifestWithMeta,
  isKryspaceRenderServiceError,
} from '../../services/kryspaceRenderService';
import { fetchControlBridgeStructureWithMeta } from '../../services/kryspaceControlBridgeStructureService';
import type { KryspaceRenderManifest } from '../../types/kryspaceRenderManifest';
import type { KryspaceControlBridgeStructure } from '../../types/kryspaceControlBridgeStructure';
import { useUser } from '../../contexts/UserContext';
import { CONTROL_BRIDGE_HOME_PATH } from '../../utils/controlBridgeRoutes';
import { resolvePersonalModelSlug } from '../../utils/resolvePersonalModelSlug';
import { ControlBridgeSlowLoadHint } from './ControlBridgeSlowLoadHint';
import { subscribeControlBridgeModelChanged } from '../../utils/chatWorkspaceToolInvocation';
import styles from './ControlBridge.module.css';

const { Paragraph } = Typography;

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'ready';
      manifest: KryspaceRenderManifest;
      structure: KryspaceControlBridgeStructure;
      devMock: boolean;
      structureDevMock: boolean;
      structureUnavailable?: boolean;
    }
  | { status: 'error'; message: string };

export const UnifiedHubControlBridgePage: React.FC = () => {
  const {
    user,
    isAuthenticated,
    isLoading: userLoading,
    availableCompanies,
    loadUserCompanies,
  } = useUser();
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>({ status: 'idle' });

  const personalSlug = useMemo(() => resolvePersonalModelSlug(user), [user]);
  const personalLabel = user?.fullName || user?.username || personalSlug || 'Personal';

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    void (async () => {
      setCompaniesLoaded(false);
      try {
        await loadUserCompanies();
      } finally {
        setCompaniesLoaded(true);
      }
    })();
  }, [user?.id, isAuthenticated, loadUserCompanies]);

  const companyLinks = useMemo(
    () =>
      (availableCompanies ?? [])
        .map((row) => {
          const company = row?.company;
          if (!company) return null;
          return {
            slug: (company.kryspace_slug ?? '').trim(),
            name: company.name?.trim() || company.kryspace_slug || 'Company',
          };
        })
        .filter((row): row is { slug: string; name: string } => Boolean(row && row.slug.length > 0)),
    [availableCompanies]
  );

  const driveRailRef = useRef<string | undefined>(undefined);
  const drivePeriodRef = useRef<string | undefined>(undefined);
  const driveViewKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    driveRailRef.current = undefined;
    drivePeriodRef.current = undefined;
    driveViewKeyRef.current = undefined;
  }, [personalSlug]);

  const load = useCallback(async () => {
    if (!personalSlug) {
      setLoadState({ status: 'idle' });
      return;
    }
    setLoadState({ status: 'loading' });
    try {
      const [manifestResult, structureResult] = await Promise.all([
        fetchRenderManifestWithMeta(personalSlug, {
          driveFilterScope: driveRailRef.current,
          drivePeriod: drivePeriodRef.current,
          driveViewKey: driveViewKeyRef.current,
        }),
        fetchControlBridgeStructureWithMeta(personalSlug),
      ]);
      setLoadState({
        status: 'ready',
        manifest: manifestResult.manifest,
        structure: structureResult.structure,
        devMock: manifestResult.devMock,
        structureDevMock: structureResult.devMock,
        structureUnavailable: structureResult.structureUnavailable,
      });
    } catch (err) {
      const message = isKryspaceRenderServiceError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Failed to load unified hub';
      setLoadState({ status: 'error', message });
    }
  }, [personalSlug]);

  const refreshDriveRail = useCallback(
    async (scope: string, opts?: { drivePeriod?: string; driveViewKey?: string }) => {
      if (!personalSlug) return;
      const rail = !scope || scope === 'company' ? undefined : scope.trim();
      driveRailRef.current = rail;
      if (opts?.drivePeriod !== undefined) {
        drivePeriodRef.current = opts.drivePeriod.trim() || undefined;
      }
      if (opts?.driveViewKey !== undefined) {
        driveViewKeyRef.current = opts.driveViewKey.trim() || undefined;
      }
      try {
        const { manifest } = await fetchRenderManifestWithMeta(personalSlug, {
          driveFilterScope: rail,
          drivePeriod: drivePeriodRef.current,
          driveViewKey: driveViewKeyRef.current,
        });
        setLoadState((prev) => (prev.status === 'ready' ? { ...prev, manifest } : prev));
      } catch {
        /* SPA still filters FlowEnvelope client-side if rail refetch fails. */
      }
    },
    [personalSlug]
  );

  const refreshAfterChatWrite = useCallback(async () => {
    if (!personalSlug) return;
    try {
      const [manifestResult, structureResult] = await Promise.all([
        fetchRenderManifestWithMeta(personalSlug, {
          driveFilterScope: driveRailRef.current,
          drivePeriod: drivePeriodRef.current,
          driveViewKey: driveViewKeyRef.current,
        }),
        fetchControlBridgeStructureWithMeta(personalSlug),
      ]);
      setLoadState({
        status: 'ready',
        manifest: manifestResult.manifest,
        structure: structureResult.structure,
        devMock: manifestResult.devMock,
        structureDevMock: structureResult.devMock,
        structureUnavailable: structureResult.structureUnavailable,
      });
    } catch {
      /* Keep the current Drive; Chat already reported the write outcome. */
    }
  }, [personalSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () => subscribeControlBridgeModelChanged(personalSlug, () => void refreshAfterChatWrite()),
    [personalSlug, refreshAfterChatWrite]
  );

  if (userLoading || !isAuthenticated) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  if (!personalSlug) {
    return (
      <div className={styles.errorPanel} data-testid="unified-hub-page">
        <Alert
          type="info"
          showIcon
          message="Personal hub unavailable"
          description={
            <>
              No personal model slug is configured for this account. Open a company Control Bridge
              instead.
            </>
          }
        />
        <Paragraph style={{ marginTop: 16 }}>
          <Link to={CONTROL_BRIDGE_HOME_PATH}>Go to company Control Bridge</Link>
        </Paragraph>
      </div>
    );
  }

  if (!companiesLoaded && loadState.status !== 'ready') {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div data-testid="unified-hub-page">
      {loadState.status === 'loading' || loadState.status === 'idle' ? (
        <div className={styles.loading} role="status" data-testid="unified-hub-loading">
          Loading hub…
          <ControlBridgeSlowLoadHint active={loadState.status === 'loading'} />
        </div>
      ) : null}

      {loadState.status === 'error' ? (
        <div className={styles.errorPanel} role="alert">
          <p>{loadState.message}</p>
          <Button type="primary" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : null}

      {loadState.status === 'ready' ? (
        <ControlBridgeShell
          manifest={loadState.manifest}
          structure={loadState.structure}
          devMock={loadState.devMock || loadState.structureDevMock}
          workspaceSlug={personalSlug}
          onManifestRefresh={load}
          onDriveRailChange={refreshDriveRail}
          unifiedHub={{
            personalSlug,
            personalLabel,
            companyLinks,
          }}
          personalLayerUser={
            user
              ? {
                  id: user.id,
                  label: personalLabel,
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
};

export default UnifiedHubControlBridgePage;
